/**
 * @fileoverview Public routes (accessible to everyone).
 * @package PlayItNow
 * @module apps/front/routes/public
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { lazy } from 'react'
import { Route, Navigate } from 'react-router-dom'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('../pages/HomePage').then((m) => ({ default: m.HomePage })))
const LandingPage = lazy(() =>
  import('../pages/LandingPage').then((m) => ({ default: m.LandingPage })),
)
const LoginPage = lazy(() => import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const RegisterPage = lazy(() =>
  import('../pages/RegisterPage').then((m) => ({ default: m.RegisterPage })),
)
const EventsListPage = lazy(() =>
  import('../pages/fan/EventsListPage').then((m) => ({ default: m.EventsListPage })),
)
// Preload EventPage chunk to avoid lazy loading issues on direct access
const EventPage = lazy(() => {
  const promise = import('../pages/fan/EventPage').then((m) => ({ default: m.EventPage }))
  promise.catch(() => {
    // Silently handle errors during preload
  })
  return promise
})
const LegalPage = lazy(() => import('../pages/LegalPage').then((m) => ({ default: m.LegalPage })))
const PrivacyPage = lazy(() =>
  import('../pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const CookiesPage = lazy(() =>
  import('../pages/CookiesPage').then((m) => ({ default: m.CookiesPage })),
)
const TermsPage = lazy(() => import('../pages/TermsPage').then((m) => ({ default: m.TermsPage })))
const ForgotPasswordPage = lazy(() =>
  import('../pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(() =>
  import('../pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const ConfirmEmailPage = lazy(() =>
  import('../pages/ConfirmEmailPage').then((m) => ({ default: m.ConfirmEmailPage })),
)
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)

/**
 * @summary Public routes.
 * Includes home, login, register, events, legal pages, and 404.
 */
export const publicRoutes = [
  /* Home */
  <Route key="home" path="/" element={<HomePage />} />,
  /* Landing (conversion-focused) */
  <Route key="landing" path="/landing" element={<LandingPage />} />,

  /* Authentication (common) */
  <Route key="login" path="/login" element={<LoginPage />} />,
  <Route key="register" path="/register" element={<RegisterPage />} />,
  <Route key="forgot-password" path="/forgot-password" element={<ForgotPasswordPage />} />,
  <Route key="reset-password" path="/reset-password" element={<ResetPasswordPage />} />,
  <Route key="confirm-email" path="/confirm-email" element={<ConfirmEmailPage />} />,

  /* Public events */
  <Route key="events" path="/events" element={<EventsListPage />} />,
  <Route key="event" path="/event/:slug" element={<EventPage />} />,

  /* Legal pages */
  <Route key="legal" path="/legal" element={<LegalPage />} />,
  <Route key="privacy" path="/privacy" element={<PrivacyPage />} />,
  <Route key="cookies" path="/cookies" element={<CookiesPage />} />,
  <Route key="terms" path="/terms" element={<TermsPage />} />,
  <Route key="terms-public" path="/terms-public" element={<Navigate to="/terms" replace />} />,

  /* 404 - Catch all unmatched routes */
  <Route key="not-found" path="*" element={<NotFoundPage />} />,
]
